/* The admin portal's twenty-one sections.
 *
 * ALL TWENTY-ONE ARE HERE so the inventory is whole, which is what the brief
 * asked for. Eight are filled from a route; eleven are `Nothing`, which states
 * what does not exist; and two -- the consumer lookup and anything that would
 * sign in as a merchant -- say so in stronger words, because a shell for those
 * would imply they are on their way and they are not.
 *
 * NO FIGURE ON ANY SCREEN IS COMPUTED HERE. Each one is a number a route
 * returned, and the route counted rows. The formatting is thousands separators
 * and dates, and nothing else.
 */
import { useEffect, useRef, useState } from "react";

import { t, type Key } from "../content";

export type Overview = {
  businesses: number;
  trading: number;
  memberships: number;
  people: number;
  stamps: number;
  rewards: number;
  stamps_30d: number;
  leads: number;
  winding_down: number;
  erasures: number;
};
export type Merchant = {
  id: string;
  name: string;
  category: string | null;
  city: string | null;
  // 048: the number the shop answers. Null for a shop enrolled before it was taken.
  contact_phone: string | null;
  joined: string | null;
  members: number;
  stamps: number;
  staff: number;
  last_activity: string | null;
  winding_down_since: string | null;
  closed_at: string | null;
};
export type Lead = {
  asked_at: string;
  asked_again_at: string | null;
  name: string | null;
  business: string | null;
  phone: string | null;
  email: string | null;
  locale: string | null;
  consented: boolean;
  doorbell_rang_at: string | null;
};
export type Compliance = {
  erasures: { day: string; count: number }[];
  consent_versions: {
    channel: string;
    locale: string;
    state: string;
    notice_version: string | null;
    approved_by: string | null;
    approved_at: string | null;
  }[];
};
export type View = { at: string; viewer: string; leads_shown: number };
export type Flag = { at: string; reason: string; business: string };
/** One of Damga's own writes (050): who, which shop and account, when. Never a link. */
export type Action = { at: string; actor: string; action: string; business: string | null; username: string | null };

/* A CODE ON A SCREEN IS A DEFECT (2026-09-18). The API sends codes; the page says
   what they mean, and a code it does not know gets a sentence saying so rather
   than the code itself. */
const REASONS: Record<string, Key> = {
  same_pair: "fraud.reason.same_pair",
  till_velocity: "fraud.reason.till_velocity",
  device_clock: "fraud.reason.device_clock",
};
export const reasonWords = (code: string): string => t(REASONS[code] ?? "fraud.reason.unknown");

const ACTIONS: Record<string, Key> = {
  provision_shop: "audit.action.provision_shop",
  owner_link: "audit.action.owner_link",
};
const actionWords = (a: Action): string =>
  t(ACTIONS[a.action] ?? "audit.action.unknown", {
    shop: a.business ?? t("audit.gone.shop"),
    username: a.username ?? t("audit.gone.account"),
  });

const n = (v: number) => v.toLocaleString("en-GB");

/** A stored category, spelled for a reader: `beach_club` is not a label.
 *  A transform rather than a hand-written map, so a category added to the
 *  database tomorrow reads correctly here without anybody remembering to. */
const kind = (v: string | null) =>
  v ? v.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase()) : "";

/** A date somebody can say out loud, in the timezone the browser is in. */
function day(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.valueOf())) return "";
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}
export function moment(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.valueOf())) return "";
  return `${day(iso)}, ${d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}`;
}

export function Nothing({ title, body, note }: { title: Key; body: Key; note?: string }) {
  return (
    <div className="nothing">
      <div className="empty">
        <p className="nothing-mark">{t("empty.what")}</p>
        <p className="empty-title">{t(title)}</p>
        <p className="empty-body">{t(body)}</p>
      </div>
      {note && <p className="fine">{note}</p>}
    </div>
  );
}

function Empty({ title, body }: { title: Key; body: Key }) {
  return (
    <div className="empty">
      <p className="empty-title">{t(title)}</p>
      <p className="empty-body">{t(body)}</p>
    </div>
  );
}

/* --- 1. Overview ------------------------------------------------------------ */

/* THREE GROUPS, NOT TEN TILES IN A ROW. Ten equal tiles wrapped seven and
 * three at 1440 and said nothing about which number answers which question.
 * These are the three questions the platform view is actually for: how much of
 * Damga exists, what has happened on it, and what is waiting for a person. */
const GROUPS: { title: Key; counts: [Key, keyof Overview][] }[] = [
  {
    title: "overview.group.size",
    counts: [
      ["overview.businesses", "businesses"],
      ["overview.trading", "trading"],
      ["overview.memberships", "memberships"],
      ["overview.people", "people"],
    ],
  },
  {
    title: "overview.group.happened",
    counts: [
      ["overview.stamps", "stamps"],
      ["overview.stamps30", "stamps_30d"],
      ["overview.rewards", "rewards"],
    ],
  },
  {
    title: "overview.group.waiting",
    counts: [
      ["overview.leads", "leads"],
      ["overview.winding", "winding_down"],
      ["overview.erasures", "erasures"],
    ],
  },
];

export function OverviewScreen({ data }: { data: Overview }) {
  return (
    <>
      <p className="lead">{t("overview.lead")}</p>
      <div className="cols">
        {GROUPS.map((g) => (
          <div className="panel" key={g.title}>
            <div className="panel-head">
              <p className="panel-title">{t(g.title)}</p>
            </div>
            <dl className="counts">
              {g.counts.map(([label, key]) => (
                <div className="count" key={label}>
                  <dt className="count-k">{t(label)}</dt>
                  <dd className="count-v">{n(data[key])}</dd>
                </div>
              ))}
            </dl>
          </div>
        ))}
      </div>
      <p className="fine">{t("overview.noMoney")}</p>
    </>
  );
}

/* --- 2. Merchants ----------------------------------------------------------- */

function OwnerLink({ onSend }: { onSend: () => Promise<string> }) {
  const [said, setSaid] = useState<string | null>(null);
  return (
    <span className="row-actions">
      <button type="button" className="btn btn-quiet" onClick={async () => setSaid(await onSend())}>
        {t("merchants.ownerLink")}
      </button>
      {said && <span className="fine" role="status">{said}</span>}
    </span>
  );
}

export function MerchantsScreen({
  merchants,
  query,
  onQuery,
  onOwnerLink,
}: {
  merchants: Merchant[];
  query: string;
  onQuery: (v: string) => void;
  /** What to say under the button: sent, or why not, in words. */
  onOwnerLink: (businessId: string) => Promise<string>;
}) {
  const needle = query.trim().toLowerCase();
  const shown = needle
    ? merchants.filter(
        (m) =>
          m.name.toLowerCase().includes(needle) ||
          (m.city ?? "").toLowerCase().includes(needle) ||
          (m.category ?? "").toLowerCase().includes(needle),
      )
    : merchants;

  if (merchants.length === 0) {
    return <Empty title="merchants.empty" body="merchants.emptyBody" />;
  }
  return (
    <>
      <p className="lead">{t("merchants.lead")}</p>
      <input
        className="input search"
        type="search"
        aria-label={t("merchants.search")}
        placeholder={t("merchants.search")}
        value={query}
        onChange={(e) => onQuery(e.target.value)}
      />
      <div className="panel">
        <div className="panel-head">
          <p className="panel-title">{t("merchants.count", { n: shown.length })}</p>
        </div>
        <div className="table-wrap">
          <table className="table-stack" role="table" data-wide="">
            <thead role="rowgroup">
              <tr role="row">
                <th role="columnheader">{t("merchants.col.name")}</th>
                <th role="columnheader">{t("merchants.col.category")}</th>
                <th role="columnheader">{t("merchants.col.number")}</th>
                <th role="columnheader">{t("merchants.col.joined")}</th>
                <th role="columnheader" className="num">{t("merchants.col.members")}</th>
                <th role="columnheader" className="num">{t("merchants.col.stamps")}</th>
                <th role="columnheader" className="num">{t("merchants.col.staff")}</th>
                <th role="columnheader">{t("merchants.col.last")}</th>
                <th role="columnheader">
                  <span className="sr-only">{t("merchants.ownerLink")}</span>
                </th>
              </tr>
            </thead>
            <tbody role="rowgroup">
              {shown.map((m) => (
                <tr role="row" key={m.id}>
                  <td role="cell" data-lead="">
                    <span className="cell-name">{m.name}</span>
                    {m.city && <span className="cell-sub">{m.city}</span>}
                    {m.closed_at ? (
                      <span className="chip" data-tone="warn">
                        {t("merchants.closed")}
                      </span>
                    ) : m.winding_down_since ? (
                      <span className="chip" data-tone="warn">
                        {t("merchants.winding")}
                      </span>
                    ) : null}
                  </td>
                  <td role="cell" data-label={t("merchants.col.category")}>{kind(m.category)}</td>
                  {/* A shop with no number on file says so rather than
                      showing an empty cell, because the gap is the point:
                      the platform cannot call it. */}
                  <td role="cell" data-label={t("merchants.col.number")} className="mono">
                    {m.contact_phone ?? <span className="fine">{t("merchants.noNumber")}</span>}
                  </td>
                  <td role="cell" data-label={t("merchants.col.joined")} className="mono">{day(m.joined)}</td>
                  <td role="cell" data-label={t("merchants.col.members")} className="num">{n(m.members)}</td>
                  <td role="cell" data-label={t("merchants.col.stamps")} className="num">{n(m.stamps)}</td>
                  <td role="cell" data-label={t("merchants.col.staff")} className="num">{n(m.staff)}</td>
                  <td role="cell" data-label={t("merchants.col.last")} className="mono">{m.last_activity ? day(m.last_activity) : t("merchants.never")}</td>
                  <td role="cell">{m.closed_at ? null : <OwnerLink onSend={() => onOwnerLink(m.id)} />}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <p className="fine">{t("merchants.noPlan")}</p>
    </>
  );
}

/* --- 3. Onboarding pipeline ------------------------------------------------- */

export function PipelineScreen({ leads }: { leads: Lead[] }) {
  return (
    <>
      <p className="lead">{t("pipeline.lead")}</p>
      {leads.length === 0 ? (
        <Empty title="pipeline.empty" body="pipeline.emptyBody" />
      ) : (
        <div className="panel">
          <div className="table-wrap">
            <table className="table-stack" role="table">
              <thead role="rowgroup">
                <tr role="row">
                  <th role="columnheader">{t("pipeline.col.asked")}</th>
                  <th role="columnheader">{t("pipeline.col.name")}</th>
                  <th role="columnheader">{t("pipeline.col.business")}</th>
                  <th role="columnheader">{t("pipeline.col.contact")}</th>
                  <th role="columnheader">{t("pipeline.col.consent")}</th>
                  <th role="columnheader">{t("pipeline.col.doorbell")}</th>
                </tr>
              </thead>
              <tbody role="rowgroup">
                {leads.map((l) => (
                  <tr role="row" key={l.asked_at + (l.phone ?? l.email ?? "")}>
                    <td role="cell" data-label={t("pipeline.col.asked")} className="mono">
                      {day(l.asked_at)}
                      {l.asked_again_at && (
                        <span className="cell-sub">
                          {t("pipeline.askedAgain", { at: day(l.asked_again_at) })}
                        </span>
                      )}
                    </td>
                    <td role="cell" data-lead="" className="cell-name">{l.name ?? ""}</td>
                    <td role="cell" data-label={t("pipeline.col.business")}>{l.business ?? ""}</td>
                    <td role="cell" data-label={t("pipeline.col.contact")} className="mono">
                      {l.phone ?? ""}
                      {l.email && <span className="cell-sub">{l.email}</span>}
                    </td>
                    <td role="cell" data-label={t("pipeline.col.consent")}>
                      <span className="chip" data-tone={l.consented ? "good" : "warn"}>
                        {l.consented ? t("pipeline.consented") : t("pipeline.notConsented")}
                      </span>
                    </td>
                    <td role="cell" data-label={t("pipeline.col.doorbell")} className="mono">{l.doorbell_rang_at ? day(l.doorbell_rang_at) : ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      <p className="fine">{t("pipeline.noStages")}</p>
    </>
  );
}

/* --- 4. Compliance and privacy ---------------------------------------------- */

export function ComplianceScreen({ data }: { data: Compliance }) {
  return (
    <>
      <p className="lead">{t("compliance.lead")}</p>
      <div className="cols">
        <div className="panel">
          <div className="panel-head">
            <p className="panel-title">{t("compliance.erasures")}</p>
          </div>
          {data.erasures.length === 0 ? (
            <p className="note">{t("compliance.noErasures")}</p>
          ) : (
            <div className="table-wrap">
              <table className="table-stack" role="table">
                <thead role="rowgroup">
                  <tr role="row">
                    <th role="columnheader">{t("compliance.day")}</th>
                    <th role="columnheader" className="num">{t("compliance.count")}</th>
                  </tr>
                </thead>
                <tbody role="rowgroup">
                  {data.erasures.map((e) => (
                    <tr role="row" key={e.day}>
                      <td role="cell" data-lead="" className="mono">{day(e.day)}</td>
                      <td role="cell" data-label={t("compliance.count")} className="num">{n(e.count)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p className="fine">{t("compliance.erasuresBody")}</p>
        </div>

        <div className="panel">
          <div className="panel-head">
            <p className="panel-title">{t("compliance.versions")}</p>
          </div>
          {data.consent_versions.length === 0 ? (
            <p className="note">{t("compliance.noVersions")}</p>
          ) : (
            <div className="table-wrap">
              <table className="table-stack" role="table">
                <thead role="rowgroup">
                  <tr role="row">
                    <th role="columnheader">{t("compliance.col.channel")}</th>
                    <th role="columnheader">{t("compliance.col.locale")}</th>
                    <th role="columnheader">{t("compliance.col.state")}</th>
                    <th role="columnheader">{t("compliance.col.notice")}</th>
                    <th role="columnheader">{t("compliance.col.approved")}</th>
                  </tr>
                </thead>
                <tbody role="rowgroup">
                  {data.consent_versions.map((v, i) => (
                    <tr role="row" key={`${v.channel}-${v.locale}-${i}`}>
                      <td role="cell" data-lead="">
                        {v.channel === "enrolment"
                          ? t("compliance.channel.enrolment")
                          : t("compliance.channel.interest")}
                      </td>
                      <td role="cell" data-label={t("compliance.col.locale")} className="mono">{v.locale}</td>
                      <td role="cell" data-label={t("compliance.col.state")}>
                        <span className="chip" data-tone={v.state === "approved" ? "good" : "warn"}>
                          {v.state}
                        </span>
                      </td>
                      <td role="cell" data-label={t("compliance.col.notice")} className="mono">{v.notice_version ?? ""}</td>
                      <td role="cell" data-label={t("compliance.col.approved")}>
                        {v.approved_by ?? <span className="fine">{t("compliance.notApproved")}</span>}
                        {v.approved_at && <span className="cell-sub">{day(v.approved_at)}</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      <p className="fine">{t("compliance.noRequests")}</p>
    </>
  );
}

/* --- 5. Audit log ----------------------------------------------------------- */

export function AuditScreen({ views, actions }: { views: View[]; actions: Action[] }) {
  return (
    <>
      <p className="lead">{t("audit.lead")}</p>
      {/* WHAT DAMGA DID, FIRST (050). The writes are what an attacker with portal
          access would use, so they lead; each line is a sentence, not a code. */}
      <div className="panel">
        <div className="panel-head">
          <p className="panel-title">{t("audit.did")}</p>
        </div>
        {actions.length === 0 ? (
          <p className="note">{t("audit.did.none")}</p>
        ) : (
          <div className="table-wrap">
            <table className="table-stack" role="table">
              <thead role="rowgroup">
                <tr role="row">
                  <th role="columnheader">{t("audit.col.at")}</th>
                  <th role="columnheader">{t("audit.col.what")}</th>
                  <th role="columnheader">{t("audit.col.viewer")}</th>
                </tr>
              </thead>
              <tbody role="rowgroup">
                {actions.map((a, i) => (
                  <tr role="row" key={`${a.at}-${i}`}>
                    <td role="cell" data-label={t("audit.col.at")} className="mono">{moment(a.at)}</td>
                    <td role="cell" data-lead="">{actionWords(a)}</td>
                    <td role="cell" data-label={t("audit.col.viewer")} className="mono">{a.actor}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="panel">
        <div className="panel-head">
          <p className="panel-title">{t("audit.looked")}</p>
        </div>
        {views.length === 0 ? (
          <p className="note">{t("audit.looked.none")}</p>
        ) : (
          <div className="table-wrap">
            <table className="table-stack" role="table">
              <thead role="rowgroup">
                <tr role="row">
                  <th role="columnheader">{t("audit.col.at")}</th>
                  <th role="columnheader">{t("audit.col.viewer")}</th>
                  <th role="columnheader" className="num">{t("audit.col.shown")}</th>
                </tr>
              </thead>
              <tbody role="rowgroup">
                {views.map((v, i) => (
                  <tr role="row" key={`${v.at}-${i}`}>
                    <td role="cell" data-label={t("audit.col.at")} className="mono">{moment(v.at)}</td>
                    <td role="cell" data-lead="" className="mono">{v.viewer}</td>
                    <td role="cell" data-label={t("audit.col.shown")} className="num">{n(v.leads_shown)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <p className="fine">{t("audit.narrow")}</p>
    </>
  );
}

/* --- 6. Flagged stamps ------------------------------------------------------ */

export function FlagsScreen({ flags }: { flags: Flag[] }) {
  return (
    <>
      <p className="lead">{t("fraud.lead")}</p>
      {flags.length === 0 ? (
        <Empty title="fraud.empty" body="fraud.emptyBody" />
      ) : (
        <div className="panel">
          <div className="table-wrap">
            <table className="table-stack" role="table">
              <thead role="rowgroup">
                <tr role="row">
                  <th role="columnheader">{t("fraud.col.at")}</th>
                  <th role="columnheader">{t("fraud.col.business")}</th>
                  <th role="columnheader">{t("fraud.col.reason")}</th>
                </tr>
              </thead>
              <tbody role="rowgroup">
                {flags.map((f, i) => (
                  <tr role="row" key={`${f.at}-${i}`}>
                    <td role="cell" data-label={t("fraud.col.at")} className="mono">{moment(f.at)}</td>
                    <td role="cell" data-lead="" className="cell-name">{f.business}</td>
                    <td role="cell" data-label={t("fraud.col.reason")}>{reasonWords(f.reason)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      <p className="fine">{t("fraud.noRules")}</p>
    </>
  );
}

/* --- 7. Infrastructure ------------------------------------------------------ */

export function InfraScreen() {
  return (
    <>
      <p className="lead">{t("infra.lead")}</p>
      <div className="facts">
        <div className="fact">
          <p className="fact-k">{t("infra.region")}</p>
          <p className="fact-v">{t("infra.regionValue")}</p>
          <p className="fact-b">{t("infra.regionBody")}</p>
        </div>
        <div className="fact">
          <p className="fact-k">{t("infra.backups")}</p>
          <p className="fact-v">{t("infra.backupsValue")}</p>
          <p className="fact-b">{t("infra.backupsBody")}</p>
        </div>
      </div>
      <p className="fine">{t("infra.noUptime")}</p>
    </>
  );
}

/* --- 8. Staff and roles ----------------------------------------------------- */

export function StaffScreen() {
  const facts: [Key, Key][] = [
    ["staff.how", "staff.howBody"],
    ["staff.oneLevel", "staff.oneLevelBody"],
    ["staff.failsClosed", "staff.failsClosedBody"],
  ];
  return (
    <>
      <p className="lead">{t("staff.lead")}</p>
      <div className="facts">
        {facts.map(([k, b]) => (
          <div className="fact" key={k}>
            <p className="fact-v">{t(k)}</p>
            <p className="fact-b">{t(b)}</p>
          </div>
        ))}
      </div>
    </>
  );
}


/* --- setting up a shop (2026-09-18) ----------------------------------------------
 * Merchant accounts are issued by Damga (DECISIONS, that date): this is where. One
 * request makes the shop, the owner's sign-in and the first till, or nothing. */
const KINDS = ["cafe", "restaurant", "gym", "barbershop", "salon", "beach_club", "hotel", "retail", "other"];

export type ProvisionInput = {
  name: string;
  category: string;
  city: string;
  contact_phone: string;
  owner_username: string;
  owner_email: string;
};

export function ProvisionScreen({
  onProvision,
}: {
  onProvision: (input: ProvisionInput) => Promise<{ ok: boolean; message: string }>;
}) {
  const blank: ProvisionInput = { name: "", category: "cafe", city: "Doha", contact_phone: "+974", owner_username: "", owner_email: "" };
  const [form, setForm] = useState<ProvisionInput>(blank);
  const [busy, setBusy] = useState(false);
  const [said, setSaid] = useState<{ ok: boolean; message: string } | null>(null);
  const set = (k: keyof ProvisionInput) => (e: { target: { value: string } }) => setForm((f) => ({ ...f, [k]: e.target.value }));
  // WHERE THE EYE IS (2026-09-18). The form empties once a shop is set up, and the
  // sentence saying so sat below it -- on a phone, below the fold of a blank form,
  // so for a moment it looked as if nothing had happened (the end-to-end test).
  // Done goes first and is brought into view; a problem stays by the button,
  // next to the fields it is about.
  const doneRef = useRef<HTMLParagraphElement | null>(null);
  useEffect(() => {
    if (said?.ok) doneRef.current?.scrollIntoView({ block: "center" });
  }, [said]);
  return (
    <>
      <p className="lead">{t("provision.lead")}</p>
      <section className="panel">
        {said?.ok && (
          <p ref={doneRef} className="form-said" role="status">
            {said.message}
          </p>
        )}
        <form
          className="form"
          noValidate
          onSubmit={async (e) => {
            e.preventDefault();
            if (busy) return;
            setBusy(true);
            const out = await onProvision({
              ...form,
              name: form.name.trim(),
              city: form.city.trim(),
              contact_phone: form.contact_phone.replace(/\s/g, ""),
              owner_username: form.owner_username.trim().toLowerCase(),
              owner_email: form.owner_email.trim(),
            });
            setSaid(out);
            if (out.ok) setForm(blank);
            setBusy(false);
          }}
        >
          <label className="field">
            <span className="field-label">{t("provision.name")}</span>
            <input className="input" value={form.name} onChange={set("name")} maxLength={120} />
          </label>
          <label className="field">
            <span className="field-label">{t("provision.kind")}</span>
            <select className="input" value={form.category} onChange={set("category")}>
              {KINDS.map((k) => (
                <option key={k} value={k}>
                  {kind(k)}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span className="field-label">{t("provision.city")}</span>
            <input className="input" value={form.city} onChange={set("city")} maxLength={80} />
          </label>
          <label className="field">
            <span className="field-label">{t("provision.number")}</span>
            <input className="input mono" inputMode="tel" value={form.contact_phone} onChange={set("contact_phone")} aria-describedby="prov-number" />
          </label>
          <p className="fine" id="prov-number">{t("provision.numberHint")}</p>
          <label className="field">
            <span className="field-label">{t("provision.username")}</span>
            <input className="input mono" autoCapitalize="none" autoCorrect="off" spellCheck={false} value={form.owner_username} onChange={set("owner_username")} aria-describedby="prov-username" />
          </label>
          <p className="fine" id="prov-username">{t("provision.usernameHint")}</p>
          <label className="field">
            <span className="field-label">{t("provision.email")}</span>
            <input className="input" type="email" autoCapitalize="none" spellCheck={false} value={form.owner_email} onChange={set("owner_email")} />
          </label>
          {said && !said.ok && (
            <p className="form-problem" role="alert">
              {said.message}
            </p>
          )}
          <button type="submit" className="btn btn-primary" aria-busy={busy}>
            {t("provision.submit")}
          </button>
        </form>
      </section>
      <p className="fine">{t("provision.next")}</p>
    </>
  );
}

/* --- staging's outbox ----------------------------------------------------------------
 * Only where the API has one, which is staging: in Doha the route answers 404 and
 * the portal does not show this screen at all. */
export type OutboxMessage = { to: string; subject: string; body: string; at: string };

export function OutboxScreen({ messages }: { messages: OutboxMessage[] }) {
  if (messages.length === 0) return <Empty title="outbox.empty" body="outbox.emptyBody" />;
  return (
    <>
      <p className="lead">{t("outbox.lead")}</p>
      {messages.map((m) => (
        <article className="panel" key={`${m.at}-${m.to}-${m.subject}`}>
          <div className="panel-head">
            <p className="panel-title">{m.subject}</p>
            <span className="fine mono">{moment(m.at)}</span>
          </div>
          <p className="fine">
            {t("outbox.to")} <span className="mono">{m.to}</span>
          </p>
          <p className="outbox-body">
            {m.body.split(/(https?:\/\/\S+)/).map((part, i) =>
              /^https?:\/\//.test(part) ? (
                <a key={i} href={part} className="outbox-link">
                  {part}
                </a>
              ) : (
                <span key={i}>{part}</span>
              ),
            )}
          </p>
        </article>
      ))}
    </>
  );
}
